import React from 'react';
import type { CollabRoomLink } from '../../collab/links';
import { useEditorUiStore } from '../../stores/editorStore';
import { dismissIncomingLink, useEscapeKey } from './collabUi';

interface CopyPromptProps {
    room: CollabRoomLink;
    onResolved: () => void;
}

export const CopyPrompt: React.FC<CopyPromptProps> = ({ room, onResolved }) => {
    const projectName = useEditorUiStore(state => state.projectName);
    const saveProject = useEditorUiStore(state => state.saveProject);
    const notify = useEditorUiStore(state => state.notify);
    const [isWaiting, setIsWaiting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const attempt = React.useRef<AbortController | null>(null);
    React.useEffect(() => () => attempt.current?.abort(), []);

    const receive = async () => {
        if (attempt.current && !attempt.current.signal.aborted) return;
        const controller = new AbortController();
        attempt.current = controller;
        setError(null);
        setIsWaiting(true);
        try {
            const { receiveCopyOffer } = await import('../../collab/session');
            controller.signal.throwIfAborted();
            await receiveCopyOffer(room, controller.signal);
            if (controller.signal.aborted) return;
            notify('Shared copy received — it is now your local project', 'info');
            onResolved();
        } catch (error) {
            if (controller.signal.aborted) return;
            setError(error instanceof Error ? error.message : 'Could not receive copy');
            attempt.current = null;
            setIsWaiting(false);
        }
    };

    const saveThenReceive = () => {
        saveProject(projectName);
        void receive();
    };

    const cancel = React.useCallback(async () => {
        attempt.current?.abort();
        dismissIncomingLink();
        setIsWaiting(false);
        onResolved();
    }, [onResolved]);

    useEscapeKey(React.useCallback(() => { void cancel(); }, [cancel]));

    return (
        <div className="collab-modal-backdrop">
            <section className="collab-modal" role="dialog" aria-modal="true" aria-labelledby="copy-title">
                <h2 id="copy-title">Get a copy of this project?</h2>
                <span className="collab-flow-badge">One-time copy</span>
                <p>Your current drawing will be replaced. The received project will not stay connected to the sender.</p>
                {isWaiting && (
                    <div className="collab-connecting-status collab-fine-print">
                        <span className="collab-pending-spinner" aria-hidden="true" />
                        <span>Waiting for the person who shared this copy…</span>
                    </div>
                )}
                {error && <p className="collab-error" role="alert">{error}</p>}
                <div className="collab-modal-actions">
                    <button type="button" className="primary-btn" autoFocus disabled={isWaiting} onClick={() => void receive()}>
                        {isWaiting ? 'Waiting…' : 'Get copy'}
                    </button>
                    <button type="button" className="secondary-btn" disabled={isWaiting} onClick={saveThenReceive}>
                        Save mine first
                    </button>
                    <button type="button" className="secondary-btn" onClick={() => { void cancel(); }}>Cancel</button>
                </div>
            </section>
        </div>
    );
};
